/**
 * aethereum directive — set or clear the team's standing order.
 *
 * A directive is one operator instruction that lands at the top of every
 * agent's get_team_context AND injects live into running sessions. It is the
 * human's lever over the swarm: "ship the auth refactor first", "freeze the
 * public API". `set` writes it; `clear` removes it.
 */

import { resolveHostedConfig, type HostedFlags } from "./hosted-config.js";
import { HostedWriteClient } from "../hive/hosted-client.js";

export interface DirectiveOptions extends HostedFlags {
  env?: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string };
  fetchImpl?: typeof fetch;
}

export async function runDirective(args: string[], options: DirectiveOptions = {}): Promise<void> {
  const sub = args[0];
  const cfg = resolveHostedConfig(options, options.env ?? process.env);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
    return;
  }

  const client = new HostedWriteClient(cfg.baseUrl, cfg.token, options.fetchImpl ?? fetch);

  try {
    if (sub === "set") {
      // Pull out `--to <handle>` (repeatable, or comma-separated) → targets, leaving the rest as the text.
      const rest = args.slice(1);
      const targets: string[] = [];
      const textParts: string[] = [];
      for (let i = 0; i < rest.length; i++) {
        if (rest[i] === "--to") {
          const v = rest[i + 1];
          if (v && !v.startsWith("--")) {
            for (const h of v.split(",")) {
              const handle = h.trim().replace(/^@/, "");
              if (handle) targets.push(handle);
            }
            i++; // consume the value
          }
        } else {
          textParts.push(rest[i]!);
        }
      }
      const text = textParts.join(" ").trim();
      if (!text) {
        console.error('Usage: aethereum directive set "<text>" [--to <handle>]');
        process.exit(1);
        return;
      }
      await client.setDirective(text, targets.length ? targets : undefined);
      console.log(
        targets.length
          ? `✓ directive set for ${targets.join(", ")} (pinned at the top of their context + live in their sessions)`
          : "✓ directive set (every agent will see it at the top of their context + live in running sessions)",
      );
      return;
    }

    if (sub === "clear") {
      await client.clearDirective();
      console.log("✓ directive cleared");
      return;
    }

    console.error('Usage: aethereum directive set "<text>" | aethereum directive clear');
    process.exit(1);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}
